import sumBy from 'lodash/sumBy';
import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';

import {Bar, BarPattern, BarSegment, Segment} from 'common/charts/visualization/bar-viz/types';
import {ValueRepresentationType} from 'common/report/constants';
import {SegmentFilterController, SegmentItem, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {OTHER_SEGMENT_VALUE} from 'common/report/util/insights/constants';
import {
  AggregatedSeriesValue,
  FlatSortingOptions,
  InsightsQueryResults,
  OrderedSumTree,
  SegmentationQueryLimits,
  SeriesSortingOptions,
  SortBy,
  UnorderedSumTree,
} from 'common/report/util/insights/types';
import {
  createUnorderedSumTree,
  getAggregatedSeriesList,
  getNestedHeaderTypes,
  getSortedAggregatedSeriesKeys,
  getSortedKeyMap,
} from 'common/report/util/insights/utils';
import {renamePropertyOld, renamePropertyValue} from 'common/report/util/properties';
import {abbreviateNumber, formatPercent} from 'common/util';
import * as mpColors from 'common/util/colors';
import {HeaderType} from 'common/util/header-sorting';

import type {LegendData, SegmentExtras} from './types';

export function getSumTree(results: InsightsQueryResults): UnorderedSumTree {
  const headerTypes = getNestedHeaderTypes(results.series);

  const sumTree = createUnorderedSumTree(results.series, results.time_comparison?.series, headerTypes);
  return sumTree;
}

export function getRelatedSegmentIdsPerSection(
  depth: number,
  aggregatedSeriesValue: AggregatedSeriesValue,
): string[][] {
  const relatedSegmentsIdsPerSection = new Array(depth).fill(null).map(() => new Set<string>());
  const {paths} = aggregatedSeriesValue;

  /**
   * All paths should be the same length.
   * Iterate over each individual path in reverse starting with the second to last segment (to exclude current segment)
   * and add to the corresponding set of selected segments for sectionIdx
   */
  let pathIdx = paths[0].length - 2;
  let sectionIdx = depth - paths[0].length + 1;

  while (pathIdx >= 0) {
    for (const path of paths) {
      relatedSegmentsIdsPerSection[sectionIdx].add(path[pathIdx]);
    }

    pathIdx--;
    sectionIdx++;
  }

  return relatedSegmentsIdsPerSection.map((set) => [...set]);
}

export function getLegendData({
  sumTree,
  headers,
  resultHeaders,
  sortConfig,
  showOther,
  queryLimits,
}: {
  sumTree: UnorderedSumTree;
  headers: string[];
  resultHeaders: string[];
  sortConfig: SeriesSortingOptions;
  showOther: boolean;
  queryLimits: SegmentationQueryLimits;
}): LegendData {
  const aggregatedSeriesList = getAggregatedSeriesList(sumTree);
  const {colSortAttrs, sortOrder} = sortConfig;

  const sortedKeyMaps = aggregatedSeriesList.map(getSortedKeyMap);
  const depth = sortedKeyMaps.length;
  const leafSortedKeyMap = sortedKeyMaps[depth - 1];

  const sections: SegmentSection[] = aggregatedSeriesList.map((series, idx) => {
    const sortOption = sortConfig.sortBy === SortBy.Value ? {sortBy: SortBy.Value, sortOrder} : colSortAttrs[idx];

    const seriesKeys = getSortedAggregatedSeriesKeys(series, sortOption as FlatSortingOptions);

    const items: SegmentItem[] = seriesKeys.map((key) => {
      return {
        id: key,
        label: renamePropertyValue(key, resultHeaders[idx]),
        color: idx === depth - 1 ? mpColors.getChartSegmentColorCombo(leafSortedKeyMap[key]).primary : null,
        relatedSegmentsPerSection: getRelatedSegmentIdsPerSection(aggregatedSeriesList.length, series[key]),
      };
    });

    if (showOther && seriesKeys.length > 1) {
      items.unshift({
        id: OTHER_SEGMENT_VALUE,
        label: `Other`,
        color: `none`,
      });
    }

    return {
      label: renamePropertyOld(headers[idx]),
      metadata: sortedKeyMaps[idx],
      items,
      // Inferring the series was truncated.
      apiResultsTruncated: seriesKeys.length >= queryLimits?.limit,
      // '12 of top 1000` doesn't make sense for bar charts
      suppressShowingTop: true,
    };
  });

  return {
    // reverse sections so that last breakdown is the first section
    sortedKeyMaps: sortedKeyMaps.reverse(),
    sections: sections.reverse(),
  };
}

/**
 * Given a sum tree and segmentFilterController, recurse through the tree and
 * filter - out any unselected segments.
 * merge  - unselected segments into the "Other" segment and override node paths with '$other_segment' in the path where applicable
 */
export function filterAndMergeUnselectedSegments(
  tree: UnorderedSumTree,
  segmentFilterController: SegmentFilterController,
): UnorderedSumTree {
  function helper(tree: UnorderedSumTree, segmentIdx: number, currPath: string[]) {
    const nodes = Object.values(tree);

    const [selectedSegments, unselectedSegments] = partition(nodes, (node) => {
      return segmentFilterController.isItemSelected(segmentIdx, node.label);
    });

    const transformedTree: UnorderedSumTree = {};

    for (const segment of selectedSegments) {
      const path = [...currPath, segment.label];
      if (segmentIdx === 0) {
        transformedTree[segment.label] = {...segment, path};
      } else {
        const children = helper(segment.children, segmentIdx - 1, path);

        if (!isEmpty(children)) {
          const childNodes = Object.values(children);
          transformedTree[segment.label] = {
            ...segment,
            children,
            value: sumBy(childNodes, `value`),
            timeComparisonValue: sumBy(childNodes, `timeComparisonValue`),
            path,
          };
        }
      }
    }

    const displayOther = segmentFilterController.isItemSelected(segmentIdx, OTHER_SEGMENT_VALUE);
    if (displayOther && unselectedSegments.length) {
      const path = [...currPath, OTHER_SEGMENT_VALUE];

      const otherSegment = {
        label: OTHER_SEGMENT_VALUE,
        path,
        headerType: HeaderType.STRING,
        value: sumBy(unselectedSegments, `value`),
        timeComparisonValue: sumBy(unselectedSegments, `timeComparisonValue`),
      };

      if (segmentIdx > 0) {
        // nested node, calculate all unselected child trees and merge them together for a nested "Other" segment
        const unselectedTrees = unselectedSegments.map((segment) => helper(segment.children, segmentIdx - 1, path));
        const mergedChildren = mergeTreesIntoOther(unselectedTrees, path);

        if (!isEmpty(mergedChildren)) {
          transformedTree[OTHER_SEGMENT_VALUE] = {
            ...otherSegment,
            path,
            children: mergedChildren,
          };
        }
      } else {
        // leaf value, only need sum of unselected segments
        transformedTree[OTHER_SEGMENT_VALUE] = otherSegment;
      }
    }

    return transformedTree;
  }

  return helper(tree, segmentFilterController.getSections().length - 1, []);
}

function getBarVizBarSegments(
  node: OrderedSumTree,
  valueRepresentationType: ValueRepresentationType,
  leafSortedKeyMap: {[key: string]: number},
  parentValue: number,
  parentTimeComparisonValue: number,
): {segment: BarSegment<SegmentExtras>; timeComparisonSegment?: BarSegment<SegmentExtras>} {
  const colors =
    node.label === OTHER_SEGMENT_VALUE
      ? mpColors.otherSegment
      : mpColors.getChartSegmentColorCombo(leafSortedKeyMap[node.label]);

  function getLabelAndValue(value, percentage): Pick<BarSegment, 'value' | 'label'> {
    return valueRepresentationType === ValueRepresentationType.Absolute
      ? {value, label: abbreviateNumber(value)}
      : {value: percentage, label: formatPercent(percentage / 100)};
  }

  const percentage = (node.value / parentValue) * 100;
  const timeComparisonPercentage = (node.timeComparisonValue / parentTimeComparisonValue || 0) * 100;

  const extras: SegmentExtras = {
    path: node.path,
    value: node.value,
    timeComparisonValue: node.timeComparisonValue,
    percentage,
    timeComparisonPercentage,
    isTimeComparisonSegment: false,
  };

  const segment = {
    extras,
    color: colors.primary,
    ...getLabelAndValue(node.value, percentage),
  };

  const timeComparisonSegment = {
    extras: {...extras, isTimeComparisonSegment: true},
    color: colors.primary,
    pattern: BarPattern.Striped,
    ...getLabelAndValue(node.timeComparisonValue, timeComparisonPercentage),
  };

  return {segment, timeComparisonSegment};
}

export function sumTreeToBarVizSegments({
  tree,
  headers,
  resultHeaders,
  leafSortedKeyMap,
  isStacked,
  isTimeComparison,
  parentValue,
  parentTimeComparisonValue,
  valueRepresentationType,
  showTotal,
}: {
  tree: OrderedSumTree[];
  headers: string[];
  resultHeaders: string[];
  leafSortedKeyMap: {[key: string]: number};
  isStacked: boolean;
  isTimeComparison: boolean;
  parentValue: number;
  parentTimeComparisonValue: number;
  valueRepresentationType: ValueRepresentationType;
  showTotal: boolean;
}): Array<Segment<SegmentExtras>> {
  return tree.map((node) => {
    const segmentProps = {
      segmentName: headers[0],
      label: node.label === OTHER_SEGMENT_VALUE ? `Other` : renamePropertyValue(node.label, resultHeaders[0]),
    };
    const abbreviatedValue = abbreviateNumber(node.value);

    // for stacked bars, stop recursing at depth - 1 to map segments
    const isStackedLeaf = isStacked && node.children && !node.children[0].children;

    if (node.children && !isStackedLeaf) {
      return {
        ...segmentProps,
        ...(showTotal ? {valueLabel: abbreviatedValue} : {}),
        children: sumTreeToBarVizSegments({
          tree: node.children,
          headers: headers.slice(1),
          resultHeaders: resultHeaders.slice(1),
          leafSortedKeyMap,
          isStacked,
          isTimeComparison,
          parentValue: node.value,
          parentTimeComparisonValue: node.timeComparisonValue,
          valueRepresentationType,
          showTotal,
        }),
      };
    } else if (isStackedLeaf) {
      const mergedSegments = node.children.map((child) =>
        getBarVizBarSegments(child, valueRepresentationType, leafSortedKeyMap, node.value, node.timeComparisonValue),
      );
      const segments = [],
        timeComparisonSegments = [];

      for (const {segment, timeComparisonSegment} of mergedSegments) {
        segments.push(segment);
        timeComparisonSegments.push(timeComparisonSegment);
      }

      const bars: Array<Bar<SegmentExtras>> = [{segments, label: abbreviatedValue}];
      if (isTimeComparison) {
        bars.unshift({
          segments: timeComparisonSegments,
          label: abbreviateNumber(node.timeComparisonValue),
        });
      }

      return {...segmentProps, bars};
    } else {
      const {segment, timeComparisonSegment} = getBarVizBarSegments(
        node,
        valueRepresentationType,
        leafSortedKeyMap,
        parentValue,
        parentTimeComparisonValue,
      );

      const bars: Array<Bar<SegmentExtras>> = [{segments: [segment]}];
      if (isTimeComparison) {
        bars.unshift({segments: [timeComparisonSegment]});
      }

      return {
        ...segmentProps,
        bars,
      };
    }
  });
}

/**
 * merge a list of unselected sum trees and override path with "other" segment
 */
function mergeTreesIntoOther(trees: UnorderedSumTree[], newPath: string[]): UnorderedSumTree {
  const mergedTree: UnorderedSumTree = {};

  for (const tree of trees) {
    for (const node of Object.values(tree)) {
      if (mergedTree[node.label]) {
        mergedTree[node.label].value += node.value;
        mergedTree[node.label].timeComparisonValue += node.timeComparisonValue;
      } else {
        const path = [...newPath, node.label];
        mergedTree[node.label] = {...node, path};
      }
    }
  }

  return mergedTree;
}
